import { isScoredInteractionMode } from "@/lib/scenarios/interactionModes";
import { useCallback, useEffect, useRef, useState } from "react";
import { KalturaAvatarSession } from "@kaltura/intelligent-agents/experience";
import {
  DEFAULT_RENDERERS,
  isKnownRuntime,
  parseWidget,
} from "@kaltura/intelligent-agents/experience/genui";
import { io } from "socket.io-client";
import { toast } from "sonner";
import { ArrowLeft, Info, WifiOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Scenario, ScoreResult } from "@/lib/powtoon-be/types";
import { kaltura } from "@/lib/scenarios/providers/kaltura/client";
import {
  findCapabilityDrift,
  kalturaCapabilities,
} from "@/lib/scenarios/providers/kaltura/capabilities";
import { kalturaSessionAdapter } from "@/lib/scenarios/providers/kaltura/sessionAdapter";
import { composeKalturaDirective } from "@/lib/scenarios/providers/kaltura/directive";
import { isPdfDisplaySource } from "@/lib/scenarios/display";
import { useKalturaVisuals } from "@/lib/scenarios/providers/kaltura/options";
import {
  startTranscript,
  appendTranscriptTurn,
  clearTranscript,
} from "@/lib/scenarios/providers/kaltura/transcriptStore";
import { PreSessionScreen } from "../PreSessionScreen";
import { SessionStartOverlay } from "../SessionStartOverlay";
import { PostSessionScreen } from "../PostSessionScreen";
import { WaitingForScoreScreen } from "../WaitingForScoreScreen";
import { ScoreScreen } from "../ScoreScreen";
import { AvatarVideoArea } from "../AvatarVideoArea";
import { SessionControls } from "../SessionControls";
import type { DocumentControl } from "../DisplayDocument";
import { PresentedDocument } from "@/components/session/PresentedDocument";
import { SessionStage } from "@/components/session/SessionStage";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { postToAncestors } from "../postToAncestors";
import { trackEvent } from "@/services/analytics";
import { GenUiAuxOverlay, isMediaKind, type GenUiWidget } from "./genui/KalturaGenUi";
import {
  KALTURA_CLOSE_DOCUMENT_TOOL,
  KALTURA_PRESENT_DOCUMENT_TOOL,
  KALTURA_READ_PAGE_TOOL,
  KALTURA_SHOW_PAGE_TOOL,
} from "@/lib/scenarios/providers/kaltura/presentationTool";
import {
  readPageResult,
  showPageResult,
  waitForPageableControl,
} from "@/lib/scenarios/providers/presentationTools";
import {
  STALL_NOTICE_DELAY_MS,
  STALL_NUDGE_TEXT,
  createSessionTrail,
  type SessionTrail,
} from "@/lib/scenarios/providers/kaltura/stallWatch";

/**
 * The one deadline that owns a blocking Kaltura tool call. Every browser-side
 * step (viewer readiness poll, page read) must finish inside it, and only one
 * answer is ever posted per invocation.
 */
const TOOL_ACK_DEADLINE_MS = 3000;

import {
  SCREEN_ANALYSIS_INTERVAL_MS,
  screenShareStartedCue,
  screenShareStoppedCue,
  startScreenFramePusher,
  type ScreenFramePusher,
} from "@/lib/scenarios/providers/kaltura/screenShare";

import {
  END_FLOW_TIMEOUT_MS,
  SCORE_FLOW_TIMEOUT_MS,
  withTimeout,
} from "@/lib/scenarios/providers/sessionTimeouts";
import { ANALYTICS_CATEGORIES, ANALYTICS_ACTIONS } from "@/config/analytics-config";
import { createLogger } from "@/lib/frontend-logger";
import { withCacheBust } from "@/lib/utils";

const logger = createLogger("kaltura.agent-frame");
/** The remote stream can lag the init handshake; retry briefly for it. */
const STREAM_POLL_INTERVAL_MS = 200;
const STREAM_POLL_TIMEOUT_MS = 30_000;
/** Upper bound on the Kaltura join handshake before we call it a stall. */
const CONNECT_TIMEOUT_MS = 45_000;

const AVATAR_CONTAINER_ID = "kaltura-agent-avatar-sink";
/**
 * Kaltura enforces `maxConversationLength` (540s by default in our agent
 * provisioning) server-side. End slightly earlier so the learner gets a proper
 * wrap-up instead of an avatar that simply goes quiet.
 */
const SESSION_CAP_MS = 525_000;
/** No speech from either side for this long — stop burning avatar minutes. */
const IDLE_TIMEOUT_MS = 180_000;
const IDLE_CHECK_INTERVAL_MS = 15_000;

/**
 * Kaltura's SDK surfaces its connect-handshake failures as bare codes. Each one
 * names a different step (see the SDK's WIRE-PROTOCOL doc), so map them to a
 * message a learner can act on while keeping the raw code in the log.
 */
const SDK_ERROR_MESSAGES: Record<string, string> = {
  ConnectionTimeout: "Could not reach Kaltura's avatar service. Please check your network and retry.",
  JoinRoomTimeout:
    "Kaltura accepted the connection but never started the conversation. Please retry — if it keeps failing, this needs reporting to Kaltura.",
  AgentResponseTimeout:
    "Kaltura connected but the avatar never joined. Please retry, and report it to Kaltura if it keeps failing.",
};

function friendlyKalturaError(raw: string): string {
  for (const [code, message] of Object.entries(SDK_ERROR_MESSAGES)) {
    if (raw.includes(code)) return message;
  }
  return raw;
}

type PageState =
  | "idle"
  | "connecting"
  | "connected"
  | "awaiting-score"
  | "scored"
  | "ended"
  | "unavailable";

/**
 * Kaltura learner runtime — **Agent** variant.
 *
 * The alternative to `KalturaFrame`: here Kaltura's Agent product owns the
 * whole conversation loop (mic capture, ASR, LLM, TTS), which is what we are
 * trialling for lower turn latency and less of the loop on our side.
 *
 * Two things make it swappable with the SDK path:
 *  - the Agent SDK is driven headlessly (we hand it a hidden <video> and lift
 *    its stream into the shared `AvatarVideoArea`), so the learner sees the
 *    exact same chrome, layouts and controls;
 *  - its live `transcript` events are appended to the same local transcript
 *    buffer, so scoring, questionnaire and role-play modes are untouched.
 *
 * Transcript durability matches the SDK path and D-ID: the buffer is in the
 * page and is persisted at end of session.
 */
export function KalturaAgentFrame({
  scenario,
  publicLinkToken,
}: {
  scenario: Scenario;
  publicLinkToken?: string;
}) {
  const content = scenario.content ?? {};
  const block = content.kaltura;
  const autoStart = !!content.auto_start;
  const redirectUrl = content.redirect_url?.trim();
  const interactionMode = block?.interaction_mode;
  const isScoredMode = isScoredInteractionMode(interactionMode);
  const display = content.display;
  const hasDisplay = !!display?.source;
  const displayLayout = display?.layout ?? "side-by-side";
  const avatarPosition: "left" | "right" = display?.avatar_position ?? "right";

  const [pageState, setPageState] = useState<PageState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [score, setScore] = useState<ScoreResult | null>(null);
  const [scoreError, setScoreError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [avatarReady, setAvatarReady] = useState(false);
  // Mirror of the stream above, readable from SDK event handlers: a cold
  // reconnect must know which (now dead) stream it is replacing.
  const remoteStreamRef = useRef<MediaStream | null>(null);
  remoteStreamRef.current = remoteStream;

  const [isEnding, setIsEnding] = useState(false);
  const [endNote, setEndNote] = useState<string | null>(null);
  // Aux GenUI widgets (web-search sources, follow-ups, link cards) overlay the
  // avatar video. Agent-pushed *media* widgets are refused outright: Kaltura
  // only ever emitted empty `content-gallery` payloads that blanked half the
  // learner's screen. The avatar presents the scenario document through our own
  // client tools instead.
  const [genuiAux, setGenuiAux] = useState<GenUiWidget | null>(null);

  /**
   * Trainer let the avatar present the scenario's display document. While on,
   * the learner starts with the avatar alone and only sees the document once
   * the `present_document` client tool fires.
   */
  const avatarPresents =
    interactionMode === "conversational" && block?.present_document === true && hasDisplay;
  const [presentOpen, setPresentOpen] = useState(false);
  /** Imperative handle on the viewer, for the paging / read tools. */
  const documentControlRef = useRef<DocumentControl | null>(null);

  /**
   * Brain-stall state. Kaltura's SDK raises `brainStalled` when it stops
   * producing a turn — the avatar just goes quiet. We nudge once, and if that
   * does not bring speech back we say so instead of leaving a frozen face.
   */
  const trailRef = useRef<SessionTrail | null>(null);
  const stallNudgedRef = useRef(false);
  const stallNoticeTimerRef = useRef<number | null>(null);
  const [stallNotice, setStallNotice] = useState(false);
  const clearStallNoticeTimer = useCallback(() => {
    if (stallNoticeTimerRef.current !== null) {
      window.clearTimeout(stallNoticeTimerRef.current);
      stallNoticeTimerRef.current = null;
    }
  }, []);


  /**
   * Trainer let the learner share their screen. Kaltura accepts no inbound
   * video track: we render the shared surface ourselves and push periodic
   * stills through the session socket for the avatar to look at.
   */
  const screenShareWanted =
    interactionMode === "conversational" && block?.screen_share_enabled === true;
  /**
   * Whether the *resolved* session actually accepts pushed stills. The trainer
   * toggle only asks for the intellect capability; if Kaltura resolves it off
   * (entitlement, or a scenario provisioned before the flag was written) the
   * button would be dead, so it isn't offered.
   */
  const [screenShareAllowed, setScreenShareAllowed] = useState(false);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const framePusherRef = useRef<ScreenFramePusher | null>(null);
  const framesSentRef = useRef(0);
  const lastScreenAnalysisAtRef = useRef(0);
  const screenAnalysisInFlightRef = useRef(false);
  const screenShareGenerationRef = useRef(0);
  /** The first description of a share is nudged; later refreshes are passive. */
  const screenNudgedRef = useRef(false);

  /**
   * Push a `shared_screen` snapshot into the brain's live context.
   *
   * `setDynamicPrompt` is fire-and-forget with no ack, so log what we handed to
   * the session (and whether it was even connected): that is the only way to
   * tell "the brain ignored the context" from "the context never left here".
   */
  const injectScreenContext = useCallback((data: Record<string, unknown>) => {
    const session = sessionRef.current;
    if (!session) {
      logger.warn("kaltura screen context skipped, no session", { status: data.status });
      return;
    }
    try {
      const payload = { shared_screen: data };
      session.setDynamicPrompt(payload);
      logger.info("kaltura screen context injected", {
        status: data.status,
        active: data.active,
        bytes: JSON.stringify(payload).length,
        page_state: pageStateRef.current,
      });
    } catch (e) {
      logger.warn("kaltura screen context injection failed", {
        error: (e as Error)?.message ?? String(e),
      });
    }
  }, []);

  /** Provoke a turn right after the context it needs is already in place. */
  const nudgeScreenShare = useCallback((cue: string) => {
    try {
      void sessionRef.current?.speak(cue);
    } catch (e) {
      logger.warn("kaltura screen share nudge failed", {
        error: (e as Error)?.message ?? String(e),
      });
    }
  }, []);

  const stopScreenShare = useCallback(() => {
    const wasSharing = Boolean(screenStreamRef.current);
    screenShareGenerationRef.current += 1;
    framePusherRef.current?.stop();
    framePusherRef.current = null;
    framesSentRef.current = 0;
    screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenStreamRef.current = null;
    setScreenStream(null);
    if (wasSharing) {
      injectScreenContext({ active: false, status: "stopped", updated_at: new Date().toISOString() });
      if (screenNudgedRef.current) nudgeScreenShare(screenShareStoppedCue());
      screenNudgedRef.current = false;
    }
  }, [injectScreenContext, nudgeScreenShare]);


  const startScreenShare = useCallback(async () => {
    if (screenStreamRef.current) {
      stopScreenShare();
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    } catch (e) {
      // The learner dismissing the picker is not an error worth surfacing.
      logger.info("kaltura screen share not started", {
        reason: (e as Error)?.name ?? String(e),
      });
      return;
    }
    screenStreamRef.current = stream;
    setScreenStream(stream);
    const generation = ++screenShareGenerationRef.current;
    lastScreenAnalysisAtRef.current = 0;
    screenNudgedRef.current = false;

    injectScreenContext({
      active: true,
      status: "analyzing_first_frame",
      updated_at: new Date().toISOString(),
    });
    // The browser's own "Stop sharing" bar bypasses our button.
    stream.getVideoTracks()[0]?.addEventListener("ended", () => stopScreenShare());
    framePusherRef.current = startScreenFramePusher({
      stream,
      send: (frame, metadata) => {
        sessionRef.current?.sendScreenShot(frame);
        framesSentRef.current += 1;
        if (framesSentRef.current === 1 || framesSentRef.current % 5 === 0) {
          logger.info("kaltura native screen frame emitted", {
            frames: framesSentRef.current,
            bytes: frame.byteLength,
            width: metadata.width,
            height: metadata.height,
          });
        }

        const now = Date.now();
        if (
          screenAnalysisInFlightRef.current ||
          now - lastScreenAnalysisAtRef.current < SCREEN_ANALYSIS_INTERVAL_MS
        ) return;
        screenAnalysisInFlightRef.current = true;
        lastScreenAnalysisAtRef.current = now;
        const startedAt = performance.now();
        void (async () => {
          try {
            const bytes = new Uint8Array(frame);
            let binary = "";
            const chunkSize = 0x8000;
            for (let i = 0; i < bytes.length; i += chunkSize) {
              binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
            }
            const result = await kaltura.describeScreen({
              image_base64: btoa(binary),
              mime: "image/jpeg",
            });
            if (generation !== screenShareGenerationRef.current || !screenStreamRef.current) return;
            injectScreenContext({
              active: true,
              status: "visible",
              description: result.description,
              analyzed_at: result.analyzed_at,
            });
            logger.info("kaltura screen description injected", {
              ms: Math.round(performance.now() - startedAt),
              description_length: result.description.length,
            });
            // First description of this share: the context alone is passive, so
            // hand the avatar a turn where it can already see the screen.
            if (!screenNudgedRef.current) {
              screenNudgedRef.current = true;
              nudgeScreenShare(screenShareStartedCue(result.description));
            }

          } catch (e) {
            logger.warn("kaltura screen analysis failed", {
              status: e instanceof Error && "status" in e ? (e as { status?: number }).status : null,
              error: (e as Error)?.message ?? String(e),
            });
          } finally {
            screenAnalysisInFlightRef.current = false;
          }
        })();
      },
      onError: (err) => {
        // A capability rejection never recovers within the session: stop the
        // capture instead of warn-looping every couple of seconds. Anything
        // else is transient — log it and keep sampling.
        if (/capabilit|not enabled|disabled|screen ?share/i.test(err.message || "")) {
          logger.warn("kaltura screen share rejected by session", { error: err.message });
          setScreenShareAllowed(false);
          stopScreenShare();
          toast.info("The avatar can't view your screen in this session.");
          return;
        }
        logger.warn("kaltura screen frame push failed", { error: err.message });
      },
    });
  }, [injectScreenContext, stopScreenShare]);


  // Never leave a capture running past the session.
  useEffect(() => () => stopScreenShare(), [stopScreenShare]);



  const sessionRef = useRef<KalturaAvatarSession | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const startedRef = useRef(false);
  /** One-shot legacy intellect activation retry. */
  const repairedRef = useRef(false);
  const trackedRef = useRef(false);
  const firstFrameRef = useRef(false);
  const pollRef = useRef<number | null>(null);
  const pageStateRef = useRef<PageState>("idle");
  /**
   * Kaltura's own `ended` / `timeExpired` events land before `handleEnd` is
   * defined, so they go through a ref — the runtime, not the learner, decides
   * when an Agent session is over.
   */
  const handleEndRef = useRef<((reason?: "time-up" | "idle") => Promise<void>) | null>(null);
  useEffect(() => {
    pageStateRef.current = pageState;
  }, [pageState]);

  const [sessionId, setSessionId] = useState<string | null>(null);

  const tracking = useSessionTracking({
    scenario,
    publicLinkToken,
    adapter: kalturaSessionAdapter,
  });

  // The Agent runtime is bootstrapped from the scenario's agent, not its face.
  const ready = !!block?.agent_id && !!block?.visual_id;

  // The catalog still for the scenario's face: a wide shot of the very scene
  // the stream is a square crop of. Used as the session backdrop so the panel
  // reads as the same room instead of a square tile on a flat surface.
  const { options: visualOptions } = useKalturaVisuals({
    enabled: ready,
    keepId: block?.visual_id,
  });
  const sceneUrl = visualOptions.find((o) => o.id === block?.visual_id)?.preview_url || undefined;

  // Kaltura owns the loop here, so "last activity" is simply the last time
  // either side spoke — fed by the SDK's own transcript/talking events.
  const lastActivityRef = useRef(Date.now());
  const getLastActivityAt = useCallback(() => lastActivityRef.current, []);
  const conversationError = null as string | null;

  // Mic state is the SDK's, not ours: mirror the control into it.
  useEffect(() => {
    const session = sessionRef.current;
    if (!session) return;
    try {
      if (isMuted) session.mute();
      else session.unmute();
    } catch (e) {
      logger.warn("kaltura agent mic toggle failed", { error: String(e) });
    }
  }, [isMuted]);

  const stopStreamPoll = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const cleanup = useCallback(async () => {
    stopStreamPoll();
    // A display capture must never outlive the session it was started for.
    stopScreenShare();
    clearStallNoticeTimer();
    setStallNotice(false);
    stallNudgedRef.current = false;
    setRemoteStream(null);
    setAvatarReady(false);
    setGenuiAux(null);
    setPresentOpen(false);
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) {
      try {
        await session.disconnect();
      } catch (e) {
        logger.warn("kaltura agent disconnect failed", { error: String(e) });
      }
    }
  }, [stopStreamPoll, stopScreenShare, clearStallNoticeTimer]);

  const failSession = useCallback(
    (message: string, extra?: Record<string, unknown>) => {
      logger.error("kaltura session failed", {
        scenario_id: scenario.id,
        error: message,
        ...extra,
      });
      startedRef.current = false;
      void cleanup();
      setError(message);
      // Every hard runtime failure lands on one retryable screen.
      pageStateRef.current = "unavailable";
      setPageState("unavailable");
    },
    [cleanup, scenario.id],
  );

  /**
   * Ask the SDK for the remote stream (`getMediaStream`). It can resolve a beat
   * after `initSession`, so retry briefly. Older SDK builds without the method
   * fall back to reading the sink <video> the SDK attaches.
   *
   * `previous` is the stream we are replacing after a cold reconnect: the SDK
   * rebuilds both peer connections, so the old stream is dead and must never be
   * accepted again — we wait for a genuinely new one.
   */
  const waitForStream = useCallback(
    (session: KalturaAvatarSession, previous?: MediaStream | null) => {
      stopStreamPoll();
      const startedAt = Date.now();

      const usable = (stream: unknown): stream is MediaStream =>
        stream instanceof MediaStream &&
        stream !== previous &&
        stream.getVideoTracks().some((t) => t.readyState === "live");

      const readStream = (): MediaStream | null => {
        // The SDK does not expose getMediaStream(). Read the active STV peer
        // connection instead: its receivers are the authoritative downlink and
        // remain available even when separate ontrack events replace the hidden
        // sink element's srcObject.
        const stv = session as unknown as {
          _pcStv?: RTCPeerConnection | null;
        };
        const receiverTracks = stv._pcStv
          ?.getReceivers()
          .map((receiver) => receiver.track)
          .filter((track) => track?.readyState === "live") ?? [];
        if (receiverTracks.some((track) => track.kind === "video")) {
          const receiverStream = new MediaStream(receiverTracks);
          if (usable(receiverStream)) return receiverStream;
        }
        const el = document
          .getElementById(AVATAR_CONTAINER_ID)
          ?.querySelector("video") as HTMLVideoElement | null;
        const stream = el?.srcObject;
        if (usable(stream)) return stream;
        return null;
      };

      // Our AvatarVideoArea owns playback and audio; keep the SDK's own sink
      // silent so the learner never hears the avatar twice.
      const muteSink = () => {
        const el = document
          .getElementById(AVATAR_CONTAINER_ID)
          ?.querySelector("video") as HTMLVideoElement | null;
        if (el) el.muted = true;
      };

      let candidateStream: MediaStream | null = null;
      const attempt = () => {
        const stream = candidateStream ?? readStream();
        if (stream && !candidateStream) {
          candidateStream = stream;
          muteSink();
          logger.info("kaltura avatar media attached", {
            source: "stv-receivers",
            tracks: stream.getTracks().map((track) => ({
              kind: track.kind,
              ready_state: track.readyState,
              muted: track.muted,
            })),
          });
          setRemoteStream(stream);
          const sink = document
            .getElementById(AVATAR_CONTAINER_ID)
            ?.querySelector("video") as HTMLVideoElement | null;
          if (sink) {
            sink.srcObject = stream;
            sink.muted = true;
            void sink.play().catch(() => undefined);
          }
        }
        const sink = document
          .getElementById(AVATAR_CONTAINER_ID)
          ?.querySelector("video") as HTMLVideoElement | null;
        if (candidateStream && sink && sink.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          stopStreamPoll();
          setAvatarReady(true);
          logger.info("kaltura avatar media playable", {
            ready_state: sink.readyState,
            video_size: `${sink.videoWidth}x${sink.videoHeight}`,
          });
          if (previous) logger.info("kaltura stream re-attached after reconnect");
          return;
        }
        if (Date.now() - startedAt > STREAM_POLL_TIMEOUT_MS) {
          stopStreamPoll();
          const stv = session as unknown as { _pcStv?: RTCPeerConnection | null };
          const receivers = stv._pcStv?.getReceivers() ?? [];
          const sink = document
            .getElementById(AVATAR_CONTAINER_ID)
            ?.querySelector("video") as HTMLVideoElement | null;
          logger.error("kaltura avatar media unavailable", {
            stv_connection_state: stv._pcStv?.connectionState ?? null,
            stv_ice_state: stv._pcStv?.iceConnectionState ?? null,
            receiver_tracks: receivers.map((receiver) => ({
              kind: receiver.track?.kind ?? null,
              ready_state: receiver.track?.readyState ?? null,
              muted: receiver.track?.muted ?? null,
            })),
            sink_has_stream: sink?.srcObject instanceof MediaStream,
            sink_ready_state: sink?.readyState ?? null,
            sink_video_size: sink ? `${sink.videoWidth}x${sink.videoHeight}` : null,
          });
          failSession(
            "The Kaltura avatar connected but never sent video. Please retry, and report it to Kaltura if it keeps failing.",
          );
        }
      };

      attempt();
      if (pollRef.current === null && (!candidateStream || !firstFrameRef.current)) {
        pollRef.current = window.setInterval(attempt, STREAM_POLL_INTERVAL_MS);
      }
    },
    [failSession, stopStreamPoll],
  );


  const startSession = useCallback(async () => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    setError(null);
    pageStateRef.current = "connecting";
    setPageState("connecting");
    // Kaltura's Agent runtime has no session id of its own that we can hold
    // before connecting, and the transcript buffer is local anyway, so the
    // session is keyed by an id we mint here.
    const localSessionId = `kaltura-agent-${crypto.randomUUID()}`;
    // True until `connect()` resolves: raises the handshake trace to a level the
    // remote transport actually forwards, so a stalled join is diagnosable.
    let connectPhase = true;

    try {
      const init = await kaltura.agentSessionInit({ agent_id: block?.agent_id ?? "" });
      logger.info("kaltura agent init", {
        widget_id: init.widget_id,
        has_partner_id: Boolean(init.partner_id),
        conversation_manager_url: init.conversation_manager_url ?? null,
        srs_base_url: init.srs_base_url ?? null,
        turn_server_url: init.turn_server_url ?? null,
        has_turn_credentials: Boolean(init.turn_credentials),
        avatars: init.avatars?.length ?? 0,
      });

      const videoEl = document
        .getElementById(AVATAR_CONTAINER_ID)
        ?.querySelector("video") as HTMLVideoElement | null;
      if (videoEl) videoEl.muted = true;

      const session = new KalturaAvatarSession({
        token: init.token,
        partnerId: init.partner_id,
        conversationManagerUrl: init.conversation_manager_url,
        // Where blocking client-tool ACKs go. Unset, the SDK uses its US prod
        // default and a `wait_for_response` tool can hang the turn.
        genieUrl: init.genie_url,
        // Break a tool loop early and warn sooner when the brain goes quiet —
        // both surface through the diagnostics logged below.
        toolSpiralLimit: 6,
        brainStallMs: 10000,
        recoverFromSpiral: true,



        srsBaseUrl: init.srs_base_url,
        turnServerUrl: init.turn_server_url,
        turnCredentials: init.turn_credentials,
        // The SDK never bundles socket.io — the transport is injected. The tap
        // below is the only view we have of the join handshake: the SDK waits
        // for `clientConfiguration` + `joinComplete` and reports a bare
        // `JoinRoomTimeout` when either never arrives.
        socketFactory: (url: string, opts: Record<string, unknown>) => {
          const socket = io(url, opts);
          const tap = socket as unknown as {
            onAny?: (fn: (event: string, ...args: unknown[]) => void) => void;
            onAnyOutgoing?: (fn: (event: string, ...args: unknown[]) => void) => void;
            on: (event: string, fn: (arg?: unknown) => void) => void;
          };
          // During the connect handshake these are the ONLY view we get of why a
          // join stalls, so they go out at info level; afterwards they are noise
          // and drop back to debug (which the transport does not forward).
          const trace = (message: string, data: Record<string, unknown>) => {
            if (connectPhase) logger.info(message, { ...data, session_id: localSessionId });
            else logger.debug(message, data);
          };
          tap.onAny?.((event) => trace("kaltura agent socket in", { event }));
          tap.onAnyOutgoing?.((event) => trace("kaltura agent socket out", { event }));
          tap.on("connect_error", (err?: unknown) =>
            logger.warn("kaltura agent socket connect_error", {
              message: (err as Error)?.message ?? String(err),
              session_id: localSessionId,
              connecting: connectPhase,
            }),
          );
          tap.on("disconnect", (reason?: unknown) =>
            logger.warn("kaltura agent socket disconnect", {
              reason: String(reason),
              session_id: localSessionId,
              connecting: connectPhase,
            }),
          );
          return socket;
        },


        videoEl,
        logger: (level: string, msg: string, data?: unknown) => {
          const details = { sdk_level: level, msg, data, session_id: localSessionId };
          if (level === "error") logger.error("kaltura agent sdk", details);
          else if (level === "warn" || level === "warning")
            logger.warn("kaltura agent sdk", details);
          else logger.info("kaltura agent sdk", details);
        },
        // Client-side policy: agent-pushed media widgets are always refused —
        // Kaltura emitted empty ones that blanked the content pane. Sources and
        // follow-ups are always allowed: web-search citations are part of the
        // answer, not a takeover.
        onAgentAction: (action) => {
          if (action.type === "render-genui" && isMediaKind(action.runtime)) return false;
        },
      });
      sessionRef.current = session;

      // Turn-level trail: what the stall report carries as its explanation.
      const trail = createSessionTrail();
      trailRef.current = trail;
      stallNudgedRef.current = false;

      session.on("avatarStartTalking", () => {
        lastActivityRef.current = Date.now();
        // Speech is the only proof a stall ended. Clear the whole stall state
        // here rather than trusting any SDK "recovered" signal.
        if (stallNudgedRef.current) {
          logger.info("kaltura brain stall recovered", {
            ms_since_stall: trail.msSinceSpeech(),
            session_id: localSessionId,
          });
          trail.record("recovered");
          stallNudgedRef.current = false;
        }
        clearStallNoticeTimer();
        setStallNotice(false);
        trail.record("speech_start");
      });
      session.on("avatarStopTalking", () => {
        lastActivityRef.current = Date.now();
        trail.record("speech_stop");
      });

      /**
       * The stall itself. Kaltura declares it; we turn it into a reported
       * failure (so it is countable in Sentry, not just a warning line), try
       * exactly one nudge, and tell the learner if that fails.
       */
      session.on("brainStalled", (data?: unknown) => {
        const d = data as { afterMs?: number; count?: number } | undefined;
        logger.error("kaltura brain stalled", {
          after_ms: d?.afterMs ?? null,
          stall_count: d?.count ?? null,
          tool_calls: trail.toolCalls(),
          ms_since_speech: trail.msSinceSpeech(),
          agent_id: block?.agent_id ?? null,
          intellect_id: block?.intellect_id ?? null,
          scenario_id: scenario.id,
          trail: trail.snapshot(),
          session_id: localSessionId,
        });
        trail.record("stall", { after_ms: d?.afterMs ?? null });

        // One nudge per stall, released again by the next speech. Repeating it
        // would race the SDK's own spiral guard.
        if (stallNudgedRef.current) return;
        stallNudgedRef.current = true;
        trail.record("nudge");
        void (async () => {
          try {
            await session.speak(STALL_NUDGE_TEXT);
            logger.info("kaltura brain stall nudge sent", { session_id: localSessionId });
          } catch (e) {
            logger.error("kaltura brain stall nudge failed", {
              error: (e as Error)?.message ?? String(e),
              session_id: localSessionId,
            });
          }
        })();

        clearStallNoticeTimer();
        stallNoticeTimerRef.current = window.setTimeout(() => {
          stallNoticeTimerRef.current = null;
          if (!stallNudgedRef.current) return; // speech already cleared it
          logger.error("kaltura brain stall unrecovered", {
            waited_ms: STALL_NOTICE_DELAY_MS,
            tool_calls: trail.toolCalls(),
            trail: trail.snapshot(),
            session_id: localSessionId,
          });
          setStallNotice(true);
        }, STALL_NOTICE_DELAY_MS);
      });
      // Kaltura generates both sides of the conversation, so its live
      // transcript events are the only record — buffer every one of them.
      session.on("transcript", (data?: unknown) => {
        const evt = (data ?? {}) as { text?: string; type?: string };
        const text = String(evt.text ?? "").trim();
        if (!text) return;
        lastActivityRef.current = Date.now();
        appendTranscriptTurn(localSessionId, evt.type === "user" ? "user" : "avatar", text);
      });
      // GenUI widgets ride the same channel as speech. Parse them with the
      // SDK's own parser + model builders and route media widgets to the
      // content area, aux widgets (sources / follow-ups / links) to the
      // overlay. Unknown runtimes are logged and ignored.
      session.on("brainSegment", (data?: unknown) => {
        const widget = parseWidget(data);
        if (!isKnownRuntime(widget.runtime)) {
          // Distinguish "Kaltura never emitted a widget" from "we ignored it".
          if (widget.runtime || widget.runtimeName || widget.widgetName) {
            logger.info("kaltura genui unknown runtime ignored", {
              runtime: widget.runtime,
              runtimeName: widget.runtimeName,
              widgetName: widget.widgetName,
            });
          }
          return;
        }
        const renderer = DEFAULT_RENDERERS[widget.runtime];
        if (!renderer) {
          logger.warn("kaltura genui no renderer for runtime", { runtime: widget.runtime });
          return;
        }
        try {
          const rendered = renderer(widget.model, { partnerId: init.partner_id });
          if (isMediaKind(rendered.kind)) {
            // Media widgets are the disabled path (Kaltura only ever emitted
            // empty ones). Logged so an upstream capability that flipped back
            // on stays visible; the avatar presents through our client tools.
            logger.warn("kaltura genui media widget ignored", {
              runtime: widget.runtime,
              kind: rendered.kind,
            });
            return;
          }
          setGenuiAux(rendered);

        } catch (e) {
          logger.warn("kaltura genui render failed", {
            runtime: widget.runtime,
            error: (e as Error)?.message ?? String(e),
          });
        }
      });
      session.on("error", (data?: unknown) => {
        const raw =
          (data as { message?: string; code?: string })?.message ||
          (data as { code?: string })?.code ||
          "Kaltura session error";
        logger.error("kaltura agent sdk error", { raw, session_id: localSessionId });
        failSession(friendlyKalturaError(raw), { session_id: localSessionId });
      });

      session.on("ended", () => {
        if (pageStateRef.current === "connected") void handleEndRef.current?.();
      });
      session.on("timeExpired", () => {
        if (pageStateRef.current === "connected") void handleEndRef.current?.("time-up");
      });

      // A cold reconnect (hard tool spiral, media/ICE failure, unresumable
      // socket) closes and rebuilds BOTH peer connections, so the stream our
      // video area is bound to is dead even though the session comes back
      // "connected" — the avatar looks and sounds permanently silent. Re-acquire
      // the stream from the new peer, and never accept the old one again.
      session.on("reconnecting", (data?: unknown) => {
        const d = data as { reason?: string; cold?: boolean; attempt?: number } | undefined;
        logger.info("kaltura agent reconnecting", {
          reason: d?.reason ?? null,
          cold: d?.cold ?? null,
          attempt: d?.attempt ?? null,
          session_id: localSessionId,
        });
      });
      session.on("reconnected", (data?: unknown) => {
        const previous = remoteStreamRef.current;
        // `recovered: true` is a warm socket resume — the media peers were never
        // torn down, so the stream we hold is still the live one. Only a cold
        // rebuild (`recovered: false`) invalidates it.
        const warm = (data as { recovered?: boolean })?.recovered === true;
        const stillLive =
          !!previous && previous.getVideoTracks().some((t) => t.readyState === "live");
        logger.info("kaltura agent reconnected", {
          recovered: (data as { recovered?: boolean })?.recovered ?? null,
          had_stream: Boolean(previous),
          still_live: stillLive,
          reattaching: !(warm && stillLive),
          session_id: localSessionId,
        });
        lastActivityRef.current = Date.now();
        if (warm && stillLive) return;
        setRemoteStream(null);
        waitForStream(session, previous);
      });

      session.on("stateChange", (data?: unknown) => {
        const state = (data as { state?: string })?.state ?? String(data ?? "");
        logger.info("kaltura agent state", {
          state,
          session_id: localSessionId,
          connecting: connectPhase,
        });
        // A handshake that ends in `disconnecting`/`disconnected` used to leave
        // the learner on a silent spinner with nothing in the log. Surface it.
        if (connectPhase && /^disconnect/i.test(state)) {
          logger.error("kaltura agent disconnected during handshake", {
            state,
            session_id: localSessionId,
          });
        }
      });



      // Client tools run in the learner's browser: the brain calls
      // `present_document` / `close_document` / the paging pair, and these
      // handlers drive our own split-screen layout and PDF viewer. Registered
      // before `connect()`. Every call is ACKed — the paging pair is created
      // `wait_for_response: true`, so a missing or failed ACK parks the brain
      // mid-turn and the avatar simply stops answering.
      if (avatarPresents) {
        type ToolCall = {
          name?: string;
          toolMetadata?: { id?: string; waitForResponse?: boolean };
        };
        let toolSequence = 0;
        let lastAcked: { sequence: number; tool: string; at: number } | null = null;

        /**
         * The sole owner of a blocking Kaltura document call, from browser work
         * through the server ACK. The one-shot guard belongs to this dispatched
         * call, never to its metadata id: Kaltura can reuse an id for adjacent
         * tool calls, and id-based deduplication drops a valid later reply.
         */
        const handleBlockingCall = async (
          call: ToolCall,
          args: unknown,
          work: () => Promise<Record<string, unknown>>,
        ) => {
          const sequence = ++toolSequence;
          const invocationId = call.toolMetadata?.id;
          const blocking = call.toolMetadata?.waitForResponse === true;
          const tool = call.name ?? "unknown";
          const startedAt = performance.now();
          let answered = false;

          logger.info("kaltura document tool call", {
            tool,
            sequence,
            invocation_id: invocationId ?? null,
            args,
            blocking,
            session_id: localSessionId,
          });
          trail.record("tool_call", { tool, seq: sequence });

          if (!blocking || !invocationId) {
            logger.error("kaltura document tool missing blocking metadata", {
              tool,
              sequence,
              invocation_id: invocationId ?? null,
              blocking,
              session_id: localSessionId,
            });
            return;
          }

          const answerOnce = async (payload: Record<string, unknown>, outcome: string) => {
            if (answered) {
              logger.warn("kaltura document tool reply dropped", {
                tool,
                sequence,
                invocation_id: invocationId,
                outcome,
                reason: "already_answered",
                session_id: localSessionId,
              });
              trail.record("tool_dropped", { tool, seq: sequence, outcome });
              return;
            }
            answered = true;
            const ackStartedAt = performance.now();
            try {
              const ack = await session.respondToTool(invocationId, payload);
              const ackMs = Math.round(performance.now() - ackStartedAt);
              const totalMs = Math.round(performance.now() - startedAt);
              if (ack?.ok) {
                lastAcked = { sequence, tool, at: Date.now() };
                logger.info("kaltura document tool reply accepted", {
                  tool,
                  sequence,
                  invocation_id: invocationId,
                  outcome,
                  ack_ms: ackMs,
                  total_ms: totalMs,
                  session_id: localSessionId,
                });
                trail.record("tool_reply", { tool, seq: sequence, ack: "accepted", outcome });
              } else {
                logger.error("kaltura document tool reply rejected", {
                  tool,
                  sequence,
                  invocation_id: invocationId,
                  outcome,
                  reason: ack?.reason ?? "unknown",
                  ack_ms: ackMs,
                  total_ms: totalMs,
                  session_id: localSessionId,
                });
                trail.record("tool_reply", { tool, seq: sequence, ack: "rejected", outcome });
              }
            } catch (e) {
              logger.error("kaltura document tool reply failed", {
                tool,
                sequence,
                invocation_id: invocationId,
                outcome,
                total_ms: Math.round(performance.now() - startedAt),
                error: (e as Error)?.message ?? String(e),
                session_id: localSessionId,
              });
              trail.record("tool_reply", { tool, seq: sequence, ack: "failed", outcome });
            }
          };

          const timer = window.setTimeout(() => {
            void answerOnce(
              {
                error: "not_ready",
                note: "The document view is not ready yet. Explain this briefly instead of retrying in this turn.",
              },
              "timeout",
            );
          }, TOOL_ACK_DEADLINE_MS);
          try {
            const payload = await work();
            await answerOnce(payload, "result");
          } catch (e) {
            await answerOnce(
              { error: (e as Error)?.message || "The document view failed." },
              "error",
            );
          } finally {
            window.clearTimeout(timer);
          }
        };



        // Diagnostics: when the avatar goes quiet these are the only signals
        // that say whether the brain looped on a tool or stalled on an ACK.
        // `brainStalled` is deliberately absent — it has its own handler above,
        // which reports it as a failure with the turn trail attached.
        for (const event of [
          "toolCall",
          "toolCallInvalid",
          "toolSpiralDetected",
          "toolSpiralRecovering",
        ]) {
          session.on(event, (data?: unknown) => {
            const call = (data as { call?: ToolCall })?.call;
            logger.warn("kaltura tool diagnostic", {
              event,
              tool: call?.name ?? (data as { name?: string })?.name ?? null,
              invocation_id: call?.toolMetadata?.id ?? null,
              last_sequence: toolSequence || null,
              session_id: localSessionId,
            });
          });
        }

        session.on("avatarStartTalking", () => {
          if (!lastAcked) return;
          logger.info("kaltura speech after document tool", {
            tool: lastAcked.tool,
            sequence: lastAcked.sequence,
            ms_after_ack: Date.now() - lastAcked.at,
            session_id: localSessionId,
          });
          lastAcked = null;
        });

        session.onToolCall(KALTURA_PRESENT_DOCUMENT_TOOL, (_args, call) => {
          setPresentOpen(true);
          logger.info("kaltura document tool handled without reply", {
            tool: call.name,
            invocation_id: call.toolMetadata?.id ?? null,
            session_id: localSessionId,
          });
          trail.record("tool_call", { tool: call.name, reply: "none" });
        });
        session.onToolCall(KALTURA_CLOSE_DOCUMENT_TOOL, (_args, call) => {
          setPresentOpen(false);
          logger.info("kaltura document tool handled without reply", {
            tool: call.name,
            invocation_id: call.toolMetadata?.id ?? null,
            session_id: localSessionId,
          });
          trail.record("tool_call", { tool: call.name, reply: "none" });
        });
        // Paging tools are only attached upstream for a PDF, but registered
        // unconditionally so a stale attachment answers honestly instead of
        // leaving the brain waiting on an unhandled call.
        session.onToolCall(KALTURA_SHOW_PAGE_TOOL, async (args, call) => {
          const requested = Number((args as { page?: unknown })?.page);
          await handleBlockingCall(call, { page: requested }, async () => {
            setPresentOpen(true);
            // The viewer may only just be mounting (document was hidden) — wait
            // briefly for it to be pageable, well inside the ACK deadline above.
            const control = await waitForPageableControl(() => documentControlRef.current);
            return showPageResult(control, requested);
          });
        });
        session.onToolCall(KALTURA_READ_PAGE_TOOL, async (_args, call) => {
          // Keyed by the page actually on screen, not by the (empty) args: a
          // repeated read of the same page replays instantly, while a read after
          // the learner turned a page still extracts fresh text.
          const at = documentControlRef.current?.currentPage() ?? 0;
          await handleBlockingCall(call, { page_at_dispatch: at }, async () => {
            const control = await waitForPageableControl(() => documentControlRef.current);
            return readPageResult(control);
          });
        });



      }


      startTranscript(localSessionId);
      firstFrameRef.current = false;
      setAvatarReady(false);
      const connectStartedAt = Date.now();
      logger.info("kaltura agent connect start", { session_id: localSessionId });
      // The SDK can hang here without ever rejecting (no `clientConfiguration`
      // / `joinComplete`, no error event). Bound it so the learner gets a real
      // message and the log gets a named failure instead of a silent spinner.
      try {
        await Promise.race([
          session.connect(),
          new Promise((_resolve, reject) =>
            window.setTimeout(
              () => reject(new Error("JoinRoomTimeout")),
              CONNECT_TIMEOUT_MS,
            ),
          ),
        ]);
      } finally {
        connectPhase = false;
      }
      logger.info("kaltura agent connect ok", {
        session_id: localSessionId,
        ms: Date.now() - connectStartedAt,
      });




      // Resolved capability surface from the server — this is where we see
      // whether the intellect-level flags actually took effect. The scenario
      // record is the source of truth, so any disagreement (only reachable via
      // an out-of-band API write) is logged and repaired for the next session.
      const want = kalturaCapabilities({ block, mode: interactionMode });
      logger.info("kaltura agent session capabilities", {
        capabilities: session.capabilities,
        want,
        knowledge_sources: block?.knowledge_source_keys?.length ?? 0,
      });

      // Arm the share button only once the server confirms it will take stills.
      // A scenario saved before the capability was written upstream resolves
      // `false` here and needs a re-save; say so instead of showing a dead
      // button.
      const shareResolved = session.capabilities?.screenShare === true;
      setScreenShareAllowed(shareResolved);
      if (screenShareWanted && !shareResolved) {
        logger.warn("kaltura screen share capability off upstream", {
          agent_id: block?.agent_id,
          intellect_id: block?.intellect_id,
        });
        toast.info("Screen sharing isn't active for this scenario yet — re-save it to enable it.");
      }
      // The composed directive, not the trainer's raw prompt: the raw one would
      // strip the presentation / screen-share instructions the save pipeline
      // wrote, leaving the avatar claiming those features are off.
      const wantDirective = composeKalturaDirective({
        system_prompt: block?.system_prompt,
        present_document: want.present_document,
        pageable: isPdfDisplaySource(display?.source),
        screen_share: want.screen_share,
      });
      const repairUpstream = (reason: string) => {
        if (!block?.agent_id) return;
        // Fire-and-forget: never block or fail the session the learner is in.
        void kaltura
          .updateAgent({
            agent_id: block.agent_id,
            intellect_id: block.intellect_id,
            name: scenario.name,
            avatar_id: block.avatar_id,
            system_prompt: wantDirective,
            opening_text: block.opening_text,
            language: block.language,
            knowledge_id: want.knowledge_id,
            web_search: want.web_search,
            screen_share: want.screen_share,
            tool_ids: block.presentation_tool_ids ?? [],
          })
          .catch((err) =>
            logger.error("kaltura reconcile failed", {
              reason,
              error: (err as Error)?.message,
            }),
          );
      };

      const drift = findCapabilityDrift(session.capabilities, want);
      if (drift.length && block?.agent_id) {
        logger.warn("kaltura capability drift", {
          drift,
          want,
          live: session.capabilities,
          agent_id: block.agent_id,
        });
        repairUpstream("capability_drift");
      } else if (block?.intellect_id) {
        // The directive text itself can go stale (an older wording, or a write
        // that pushed the raw prompt). Read it back and repair without asking
        // the trainer for a manual re-save.
        void kaltura
          .probeState({ intellect_id: block.intellect_id })
          .then((state) => {
            const live = (state?.intellect as { base_directive?: string } | undefined)?.base_directive ?? "";
            if (live.trim() === wantDirective.trim()) return;
            logger.warn("kaltura directive drift", {
              intellect_id: block.intellect_id,
              live_length: live.length,
              want_length: wantDirective.length,
            });
            repairUpstream("directive_drift");
          })
          .catch((err) =>
            logger.warn("kaltura directive read-back failed", {
              error: (err as Error)?.message,
            }),
          );
      }


      sessionIdRef.current = localSessionId;
      setSessionId(localSessionId);
      lastActivityRef.current = Date.now();
      waitForStream(session);
      pageStateRef.current = "connected";
      setPageState("connected");
      if (isMuted) {
        try {
          session.mute();
        } catch {
          /* the control simply stays visually muted */
        }
      }

    } catch (e) {
      const raw = (e as Error).message || "Failed to start the Kaltura session.";
      logger.error("kaltura agent start failed", { raw, code: (e as { code?: string })?.code });
      // Scenarios saved before the intellect status fix can stall
      // the join handshake. Re-push the stored prompt once through
      // `update_agent` — that activates the intellect — and retry the session.
      if (!repairedRef.current && block?.agent_id && /joinroom|timed out|timeout/i.test(raw)) {
        repairedRef.current = true;
        try {
          logger.warn("kaltura agent repairing intellect then retrying", {
            agent_id: block.agent_id,
          });
          // The update full-replaces the capabilities sub-dict and the
          // knowledge attach list, so the complete state goes out — derived
          // from the same helper the save pipeline uses.
          const repairState = kalturaCapabilities({ block, mode: interactionMode });
          await kaltura.updateAgent({
            agent_id: block.agent_id,
            intellect_id: block.intellect_id,
            name: scenario.name,
            avatar_id: block.avatar_id,
            system_prompt: composeKalturaDirective({
              system_prompt: block.system_prompt,
              present_document: repairState.present_document,
              pageable: isPdfDisplaySource(display?.source),
              screen_share: repairState.screen_share,
            }),
            opening_text: block.opening_text,
            language: block.language,
            knowledge_id: repairState.knowledge_id,
            web_search: repairState.web_search,
            screen_share: repairState.screen_share,
            tool_ids: block.presentation_tool_ids ?? [],
          });
          try {
            sessionRef.current?.disconnect?.();
          } catch {
            /* the stalled socket is being replaced anyway */
          }
          sessionRef.current = null;
          startedRef.current = false;
          await startSessionRef.current?.();
          return;
        } catch (repairErr) {
          logger.error("kaltura agent repair failed", {
            error: (repairErr as Error)?.message,
          });
        }
      }
      failSession(friendlyKalturaError(raw));
    }
  }, [
    ready,
    block,
    failSession,
    interactionMode,
    isMuted,
    scenario,
    tracking,
    waitForStream,
    avatarPresents,
  ]);


  // Lets the one-shot repair path above re-enter `startSession` without a
  // circular dependency in the callback.
  const startSessionRef = useRef<typeof startSession | null>(null);
  useEffect(() => {
    startSessionRef.current = startSession;
  }, [startSession]);

  const handleEnd = useCallback(
    async (reason?: "time-up" | "idle") => {
      const sid = sessionIdRef.current;
      setIsEnding(true);
      if (reason === "time-up") {
        setEndNote("Time is up — this session reached its maximum length.");
      } else if (reason === "idle") {
        setEndNote("The session ended because there was no activity for a while.");
      }
      tracking.markEnded();
      trackEvent({
        category: ANALYTICS_CATEGORIES.SESSION,
        action: ANALYTICS_ACTIONS.COMPLETED,
        ga4EventName: "session_completed",
        extraData: { scenario_id: scenario.id, session_id: sid, provider: "kaltura" },
      });
      if (isScoredMode) {
        setScore(null);
        setScoreError(null);
        pageStateRef.current = "awaiting-score";
        setPageState("awaiting-score");
      }

      // Tear down media *before* reading the transcript — the buffer is local,
      // so unlike the polled providers there is nothing left to arrive.
      try {
        await withTimeout(cleanup(), END_FLOW_TIMEOUT_MS, "Timed out while ending Kaltura session");
      } catch (e) {
        logger.warn("kaltura cleanup timed out or failed", {
          scenario_id: scenario.id,
          session_id: sid,
          error: (e as Error)?.message ?? String(e),
        });
      }

      let transcriptObjectId: string | null = null;
      if (sid && !trackedRef.current) {
        trackedRef.current = true;
        try {
          const res = await withTimeout(
            tracking.trackOnEnd({ sessionId: sid }),
            END_FLOW_TIMEOUT_MS,
            "Timed out while saving Kaltura session transcript",
          );
          transcriptObjectId = res.transcriptObjectId;
        } catch (e) {
          const message =
            "We couldn't save your transcript in time, so your score can't be calculated right now. Please try again later or contact your trainer.";
          logger.error("kaltura tracking failed", {
            scenario_id: scenario.id,
            session_id: sid,
            error: (e as Error)?.message ?? String(e),
          });
          if (isScoredMode) {
            setScoreError(message);
            pageStateRef.current = "scored";
            setPageState("scored");
          } else {
            setError(message);
            pageStateRef.current = "ended";
            setPageState("ended");
          }
          postToAncestors({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
          return;
        }
      }
      if (sid) clearTranscript(sid);

      if (redirectUrl) {
        postToAncestors({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
        window.location.href = redirectUrl;
        return;
      }

      if (isScoredMode && transcriptObjectId) {
        let scoring: Awaited<ReturnType<typeof tracking.runScoringJob>>;
        try {
          scoring = await withTimeout(
            tracking.runScoringJob(transcriptObjectId),
            SCORE_FLOW_TIMEOUT_MS,
            "Timed out while waiting for Kaltura score",
          );
        } catch (e) {
          logger.error("kaltura scoring timed out or failed", {
            scenario_id: scenario.id,
            session_id: sid,
            transcript_object_id: transcriptObjectId,
            error: (e as Error)?.message ?? String(e),
          });
          scoring = {
            result: null,
            error:
              "We couldn't score your session right now. Please try again later or contact your trainer if the problem persists.",
            aborted: false,
          };
        }
        const { result, error: sErr, aborted } = scoring;
        if (aborted) return;
        if (result) {
          setScore(result);
          postToAncestors({
            event: "sessionScored",
            sessionId: sid,
            scenarioId: scenario.id,
            score: result,
          });
        } else if (sErr) {
          setScoreError(sErr);
        }
        pageStateRef.current = "scored";
        setPageState("scored");
        postToAncestors({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
        return;
      }

      if (isScoredMode && !transcriptObjectId) {
        setScoreError(
          "We saved your session but couldn't find the transcript object needed for scoring. Please try again later or contact your trainer.",
        );
        pageStateRef.current = "scored";
        setPageState("scored");
        postToAncestors({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
        return;
      }

      postToAncestors({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
      pageStateRef.current = "ended";
      setPageState("ended");
    },
    [cleanup, isScoredMode, redirectUrl, scenario.id, tracking],
  );

  useEffect(() => {
    handleEndRef.current = handleEnd;
  }, [handleEnd]);

  const handleRestart = useCallback(async () => {
    await cleanup();
    startedRef.current = false;
    trackedRef.current = false;
    firstFrameRef.current = false;
    sessionIdRef.current = null;
    setSessionId(null);
    setAvatarReady(false);
    setScore(null);
    setScoreError(null);
    setIsEnding(false);
    setError(null);
    setEndNote(null);
    setGenuiAux(null);
    // A restart hides the document again — the avatar has to present it anew.
    setPresentOpen(false);
    tracking.reset();
    pageStateRef.current = "idle";
    setPageState("idle");
  }, [cleanup, tracking]);

  const handleBack = useCallback(() => {
    postToAncestors({ event: "sessionBack", scenarioId: scenario.id });
    if (typeof window !== "undefined" && window.parent === window) {
      try {
        window.history.back();
      } catch {
        /* ignore */
      }
    }
  }, [scenario.id]);

  useEffect(() => {
    return () => {
      void cleanup();
    };
  }, [cleanup]);

  /**
   * Hard cap + idle guard. Both run the normal end flow, so the transcript is
   * still saved and scored exactly as with a manual end.
   */
  useEffect(() => {
    if (pageState !== "connected" || isEnding) return;
    const cap = window.setTimeout(() => void handleEnd("time-up"), SESSION_CAP_MS);
    const idle = window.setInterval(() => {
      if (Date.now() - getLastActivityAt() >= IDLE_TIMEOUT_MS) {
        void handleEnd("idle");
      }
    }, IDLE_CHECK_INTERVAL_MS);
    return () => {
      window.clearTimeout(cap);
      window.clearInterval(idle);
    };
  }, [pageState, isEnding, handleEnd, getLastActivityAt]);

  // Best-effort persistence when the tab closes mid-session.
  useEffect(() => {
    const handler = () => {
      const st = pageStateRef.current;
      const live = st === "connecting" || st === "connected";
      // Release the Kaltura concurrency slot instead of waiting for the
      // server-side reaper to notice the abandoned tab.
      try {
        void sessionRef.current?.disconnect();
      } catch {
        /* ignore */
      }
      if (!live || trackedRef.current) return;
      trackedRef.current = true;
      tracking.beaconOnUnload(sessionIdRef.current);
    };
    window.addEventListener("pagehide", handler);
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("pagehide", handler);
      window.removeEventListener("beforeunload", handler);
    };
  }, [tracking]);

  if (!ready) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border bg-card p-6 text-center">
          <Info className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-lg font-semibold">This scenario is misconfigured</h2>
          <p className="text-sm text-muted-foreground">
            No Kaltura avatar is attached. Open the editor and save the scenario again.
          </p>
        </div>
      </div>
    );
  }

  if (pageState === "awaiting-score") return <WaitingForScoreScreen scenario={scenario} />;

  if (pageState === "scored") {
    return (
      <ScoreScreen scenario={scenario} score={score} error={scoreError} onRestart={handleRestart} />
    );
  }

  if (pageState === "unavailable") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="max-w-md space-y-4 rounded-lg border bg-card p-6 text-center">
          <WifiOff className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-lg font-semibold">Session unavailable</h2>
          <p className="text-sm text-muted-foreground">
            {error ?? "We couldn't start the avatar session right now."}
          </p>
          <div className="flex justify-center gap-2">
            <Button onClick={() => void handleRestart()}>Try again</Button>
            <Button variant="secondary" onClick={handleBack}>
              Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === "ended") {
    return (
      <PostSessionScreen
        scenario={scenario}
        subtitle={error ?? endNote ?? conversationError ?? undefined}
        onRestart={handleRestart}
      />
    );
  }

  // Teardown / transcript / scoring window: show the post-session screen with
  // restart disabled instead of falling through to the start screen.
  if (isEnding) {
    return <PostSessionScreen scenario={scenario} onRestart={handleRestart} busy />;
  }

  const hasVideo =
    !!remoteStream && remoteStream.getVideoTracks().some((t) => t.readyState === "live");
  const isLive = pageState === "connected" && hasVideo && avatarReady && !isEnding;
  // "Connected but no video frame yet" is still starting up: keep the loading
  // state so the interactive pre-session screen never flashes back.
  const isConnecting =
    pageState === "connecting" ||
    (pageState === "connected" && (!hasVideo || !avatarReady) && !isEnding);

  // Kaltura's SDK takes no inbound media track, so there is no camera to
  // publish — omitting that handler hides the button entirely. Screen share is
  // offered only when the trainer enabled it *and* the resolved session says it
  // accepts pushed stills: it is a local capture we sample into stills, not a
  // published track.
  const commonControlsProps = {
    isMuted,
    onToggleMute: () => setIsMuted((v) => !v),
    onEnd: () => void handleEnd(),
    ...(screenShareWanted && screenShareAllowed
      ? {
          isScreenSharing: !!screenStream,
          onToggleScreenShare: () => void startScreenShare(),
        }
      : {}),
  };

  // Kaltura streams carry their own baked-in background (the visual catalog
  // item owns it), so the only correct treatment is an aspect-matched tile:
  // one video element, nothing cropped, nothing duplicated behind it.
  const renderAvatar = (fit: "native" | "scene-fill" = "native") => (
    <AvatarVideoArea
      previewUrl={
        fit === "scene-fill" && sceneUrl
          ? sceneUrl
          : withCacheBust(scenario.thumb_url, scenario.modified)
      }
      mediaStream={remoteStream}
      errorMessage={conversationError}
      fit={fit}
      onNativeRatio={(ratio) =>
        logger.debug("kaltura stream ratio", {
          ratio: Number(ratio.toFixed(3)),
          hasSceneStill: !!sceneUrl,
        })
      }
      onFirstFrame={() => {
        if (firstFrameRef.current) return;
        firstFrameRef.current = true;
        setAvatarReady(true);
        logger.info("kaltura avatar first frame", { session_id: sessionIdRef.current });
        tracking.markStarted();
        postToAncestors({
          event: "sessionStarted",
          sessionId: sessionIdRef.current,
          scenarioId: scenario.id,
        });
        trackEvent({
          category: ANALYTICS_CATEGORIES.SESSION,
          action: ANALYTICS_ACTIONS.STARTED,
          ga4EventName: "session_started",
          extraData: {
            scenario_id: scenario.id,
            session_id: sessionIdRef.current,
            provider: "kaltura",
            runtime: "agent",
            mode: interactionMode ?? "conversational",
          },
        });
      }}
      {...commonControlsProps}
      hideControls
    />
  );

  // With the presentation capability on, the document stays hidden until the
  // avatar calls `present_document`; otherwise it is up for the whole session.
  const showDocument = hasDisplay && (!avatarPresents || presentOpen);
  // A live screen share takes the document pane: it is what the learner and the
  // avatar are looking at together.
  const contentNode = screenStream ? (
    <div className="h-full w-full bg-black">
      <video
        className="h-full w-full object-contain"
        autoPlay
        muted
        playsInline
        ref={(el) => {
          if (el && el.srcObject !== screenStream) el.srcObject = screenStream;
        }}
      />
    </div>
  ) : showDocument && display?.source ? (
      <PresentedDocument
        scenarioId={scenario.id}
        source={display.source}
        publicLinkToken={publicLinkToken}
        controlRef={documentControlRef}
        onClose={avatarPresents ? () => setPresentOpen(false) : undefined}
      />
    ) : null;

  return (
    <div className="relative flex h-[100dvh] w-screen bg-background">
      {/* The SDK requires a container it can attach its own <video> to. It is
          kept off-screen (not display:none, which stops playback) and its
          stream is mirrored into AvatarVideoArea. */}
      <div
        id={AVATAR_CONTAINER_ID}
        aria-hidden="true"
        className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
      >
        <video muted playsInline />
      </div>

      {!isConnecting && !isEnding && !isLive && (
        <Button
          onClick={handleBack}
          size="icon"
          variant="secondary"
          className="absolute top-4 left-4 z-20 rounded-lg bg-card/70 backdrop-blur"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      )}

      {isLive ? (
        <div className="relative flex-1 min-h-0 flex flex-col">
          <SessionStage
            documentNode={contentNode}
            layout={displayLayout}
            avatarPosition={avatarPosition}
            renderAvatar={(slot) => renderAvatar(slot === "bubble" ? "native" : "scene-fill")}
          />
          {/* Aux GenUI widgets (web-search sources, follow-ups, link cards)
              overlay the session; tapping a follow-up asks it via speak(),
              the SDK's brain-reasoned text channel. */}
          {genuiAux && (
            <div className="absolute left-4 bottom-24 z-20 w-80 max-w-[calc(100%-2rem)] space-y-2">
              <GenUiAuxOverlay
                widget={genuiAux}
                onFollowup={(q) => {
                  try {
                    void sessionRef.current?.speak(q);
                  } catch (e) {
                    logger.warn("kaltura follow-up send failed", {
                      error: (e as Error)?.message ?? String(e),
                    });
                  }
                }}
              />
            </div>
          )}
          {/* The avatar stopped mid-conversation and our nudge did not bring it
              back. Say so plainly and offer the same nudge by hand, rather than
              leaving the learner talking to a silent face. */}
          {stallNotice && (
            <div className="absolute top-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 rounded-lg border border-border bg-card/95 px-4 py-2 shadow-lg backdrop-blur">
              <Info className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm text-card-foreground">
                The avatar stopped responding.
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setStallNotice(false);
                  trailRef.current?.record("nudge", { source: "manual" });
                  try {
                    void sessionRef.current?.speak(STALL_NUDGE_TEXT);
                  } catch (e) {
                    logger.warn("kaltura manual stall nudge failed", {
                      error: (e as Error)?.message ?? String(e),
                    });
                  }
                }}
              >
                Continue
              </Button>
            </div>
          )}
          <SessionControls
            {...commonControlsProps}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30"
          />
        </div>
      ) : autoStart ? (
        <div className="absolute inset-0 z-10">
          <SessionStartOverlay
            previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
            busy={isConnecting}
            onStart={() => void startSession()}
            errorMessage={error}
          />
        </div>
      ) : (
        <div className="absolute inset-0 z-10">
          <PreSessionScreen
            scenario={scenario}
            connecting={isConnecting}
            onStart={() => void startSession()}
            onBack={handleBack}
          />
        </div>
      )}
    </div>
  );
}
